import React from "react";
import type { Professional } from "../../../services/professionalsApi";
import { DataGrid, GridAction, Avatar } from "../../shared";
import { Icons } from "../../shared/Icons";
import { displayPhoneNumber } from "../../../utils/phoneUtils";

interface ProfessionalsGridViewProps {
  professionals: Professional[];
  onEdit: (professional: Professional) => void;
  onDelete?: (professionalId: number) => void;
  onManageAvailability: (professional: Professional) => void;
  onResetPassword: (professional: Professional) => void;
  loading?: boolean;
  companyCategory?: "car" | "barbershop";
  isProfessional?: boolean;
}

export const ProfessionalsGridView: React.FC<ProfessionalsGridViewProps> = ({
  professionals,
  onEdit,
  onDelete,
  onManageAvailability,
  onResetPassword,
  loading = false,
  companyCategory,
  isProfessional = false,
}) => {
  const renderProfessionalCard = (professional: Professional) => (
    <div
      className="bg-white dark:bg-slate-800 border-2 rounded-2xl shadow-medium hover:shadow-large 
                transition-all duration-300 transform hover:scale-[1.01] overflow-hidden 
                border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500"
    >
      {/* Professional Card Header */}
      <div className="p-6 border-b border-gray-200 dark:border-slate-700">
        <div className="flex items-center gap-4">
          <Avatar
            text={professional.name}
            src={professional.picture}
            size="xl"
            variant="blue"
          />
          <div className="flex-1 flex items-center">
            <h3 className="font-bold text-gray-900">{professional.name}</h3>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onEdit(professional)}
              disabled={loading}
              className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg 
                       transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Editar Profissional"
            >
              <Icons.Edit sx={{ fontSize: 20 }} />
            </button>
            <button
              onClick={() => onResetPassword(professional)}
              disabled={loading}
              className="p-2 text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded-lg 
                       transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Alterar Senha"
            >
              <Icons.Lock sx={{ fontSize: 20 }} />
            </button>
            {onDelete && !isProfessional && (
              <button
                onClick={() => onDelete(professional.id)}
                disabled={loading}
                className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg 
                         transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Excluir Profissional"
              >
                <Icons.Delete sx={{ fontSize: 20 }} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Professional Card Body */}
      <div className="p-6 space-y-4">
        <div className="space-y-3">
          {professional.email && (
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-500">E-mail:</span>
              <span className="text-sm font-semibold text-gray-900 break-all">
                {professional.email}
              </span>
            </div>
          )}

          {professional.phoneNumber && (
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-500">
                Telefone:
              </span>
              <span className="text-sm font-semibold text-gray-900">
                {displayPhoneNumber(professional.phoneNumber)}
              </span>
            </div>
          )}

          {companyCategory === "barbershop" && (
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-500">
                Horário:
              </span>
              <button
                onClick={() => onManageAvailability(professional)}
                className="text-sm font-semibold text-purple-600 bg-purple-50 hover:bg-purple-100 px-3 py-1 rounded-lg transition-colors duration-200"
              >
                Gerenciar
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const actions: GridAction<Professional>[] = [
    {
      label: "Editar profissional",
      icon: <Icons.Edit sx={{ fontSize: 18 }} />,
      onClick: (professional) => onEdit(professional),
      className:
        "p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors duration-200",
      title: "Editar profissional",
      disabled: () => loading,
    },
    {
      label: "Alterar senha",
      icon: <Icons.Lock sx={{ fontSize: 18 }} />,
      onClick: (professional) => onResetPassword(professional),
      className:
        "p-2 text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded-lg transition-colors duration-200",
      title: "Alterar senha",
      disabled: () => loading,
    },
    ...(onDelete && !isProfessional
      ? [
          {
            label: "Excluir profissional",
            icon: <Icons.Delete sx={{ fontSize: 18 }} />,
            onClick: (professional: Professional) => onDelete(professional.id),
            className:
              "p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors duration-200",
            title: "Excluir profissional",
            disabled: () => loading,
          } as GridAction<Professional>,
        ]
      : []),
    {
      label: "Gerenciar disponibilidade",
      icon: <Icons.Calendar sx={{ fontSize: 18 }} />,
      onClick: (professional) => onManageAvailability(professional),
      className:
        "p-2 text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded-lg transition-colors duration-200",
      title: "Gerenciar disponibilidade",
    },
  ];

  return (
    <DataGrid<Professional>
      data={professionals}
      renderCard={renderProfessionalCard}
      actions={actions}
      keyExtractor={(professional) => professional.id}
      emptyMessage="Nenhum profissional encontrado"
      loading={loading}
      containerClassName="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
    />
  );
};
